import { customElement, property } from "lit/decorators.js";
import { html, LitElement, css } from "lit";
import { Parameter as EtlParameter } from "@reporting/rp-types/etl";
import { Parameter as ExtParameter } from "@reporting/rp-types/ext";
import { SerializedValue, TreeAdapter, TreeDropdown } from "@omegagrid/tree";
import { FormTextInput, FormNumericInput, FormSwitch, CalendarDateInput } from "@omegagrid/form";
import { UIValueType, dom, events } from "@omegagrid/core";
import { choose } from "lit/directives/choose.js";
import { createRef, ref } from "lit/directives/ref.js";

function getPrameterTypeFromSqlType(sqlType: string, isList?: boolean): UIValueType {
	if (isList) {
		return /^table/i.test(sqlType) ? 'm' : 's';
	} else if (/^table/i.test(sqlType)) {
		return 't';
	} else if (/bit/i.test(sqlType)) {
		return 'b';
	} else if (/int/i.test(sqlType)) {
		return 'n';
	} else if (/date/i.test(sqlType)) {
		return 'd';
	} else {
		return 't';
	}
}

export type CreateCustomParameterOptions = {
	mode: 'user'|'dev';
};

export function createCustomParameterFromSqlParameter(sqlParameter: EtlParameter|ExtParameter, attrs?: any, options?: CreateCustomParameterOptions) {
	options = Object.assign({ mode: 'user' }, options);
	const value = sqlParameter.value ?? (sqlParameter.default_value_sql ? null : sqlParameter.default_value);
	return dom.createElement<CustomParameter>('rp-custom-parameter', {
		placeholder: options.mode === 'dev' ? sqlParameter.default_value || '' : '',
		value: value,
		type: getPrameterTypeFromSqlType(sqlParameter.data_type, sqlParameter.id_thread_list != null),
		...attrs
	});
}


@customElement('rp-custom-parameter')
export class CustomParameter extends LitElement {

	static styles = css`
		:host {
			display: block;
		}

		og-input {
			display: block;
		}

		:host > * {
			height: 100%;
		}

		og-form-checkbox {
			margin-top: 4px;
		}

		og-tree-dropdown, og-input {
			border: 1px solid var(--og-colortype-accent-border-color);
			color: var(--og-text-color-3);
		}

		og-tree-dropdown {
			background-color: var(--og-accent-color-alpha-30);
		}
	`;

	@property({type: String})
	type: UIValueType;

	@property({type: String})
	placeholder: string;

	@property({type: Object})
	treeAdapter: TreeAdapter;

	componentRef = createRef<HTMLElement>();
	get component() { return this.componentRef?.value }

	private _value: unknown = null;
	@property({attribute: false})
	set value(value: unknown) {
		this._value = value;
		this.requestUpdate();
	}
	get value() {
		if (!this.component) return this._value;
		let value: unknown = null;
		switch(this.type) {
			// read the selection - the cross button clears it, but leaves the value property as it was
			case 's':
				value = (this.component as TreeDropdown).selection.serialize();
				return value && Array.isArray(value) && value[0] && value[0][0] ? value[0][0] : null;
			case 'm':
				return (this.component as TreeDropdown).selection.serialize();
			case 't':
				return (this.component as FormTextInput).value;
			case 'n':
				return (this.component as FormNumericInput).value;
			case 'b':
				return (this.component as FormSwitch).value;
			case 'd':
				return (this.component as CalendarDateInput).value;
			default: return null;
		}
	}

	

	get normalizedTreeValue(): SerializedValue[] {
		if (this._value == null || this._value === '') return null;
		if (Array.isArray(this._value)) return this.normalizeTreeRows(this._value);
		if (typeof this._value !== 'string') return null;

		if (this._value.charAt(0) == '[') {
			try {
				return this.normalizeTreeRows(JSON.parse(this._value));
			} catch (error) {
				return [[this._value, 0, 1]];
			}
		} else {
			return [[this._value, 0, 1]];
		}
	}

	// a table default comes as bare rows ([["a"],["b"]]) - the first column is the key;
	// a serialized tree value carries a numeric level in the second place
	private normalizeTreeRows(rows: unknown[]): SerializedValue[] {
		return rows.map(row => Array.isArray(row) && typeof row[1] !== 'number'
			? [String(row[0]), 0, 1]
			: row as SerializedValue);
	}

	get normalizedBoolValue(): boolean {
		if (this._value == null || this._value === '' || this._value === false || this._value === '0' || this._value === 0) return false;
		return true;
	}

	_onChange = () => {
		this.dispatchEvent(new events.ChangeEvent({value: this.value}));
	}

	render = () => html`
		${choose(this.type, [
			['m', () => html`
				<og-tree-dropdown ${ref(this.componentRef)}
					@change="${this._onChange}"
					color="transparent"
					.adapter="${this.treeAdapter}"
					.value="${this.normalizedTreeValue}"
					cross>
				</og-tree-dropdown>
			`],
			['s', () => html`
				<og-tree-dropdown ${ref(this.componentRef)}
					@change="${this._onChange}"
					color="transparent"
					.adapter="${this.treeAdapter}"
					.value="${this.normalizedTreeValue}"
					cross>
				</og-tree-dropdown>
			`],
			['t', () => html`
				<og-input ${ref(this.componentRef)}
					.value="${this._value ?? ''}"
					placeholder="${this.placeholder ?? ''}"
					@change="${this._onChange}">
				</og-input>
			`],
		 	['n', () => html`
				<og-form-numericinput ${ref(this.componentRef)}
					.value="${this._value}"
					@change="${this._onChange}">
				</og-form-numericinput>
		 	`],
			['b', () => html`
				<og-form-switch ${ref(this.componentRef)}
					.value="${this.normalizedBoolValue}"
					@change="${this._onChange}">
				</og-form-switch>
			`],
			['d', () => html`
				<og-form-calendardateinput ${ref(this.componentRef)}
					.value="${this._value || null}"
					@change="${this._onChange}">
				</og-form-calendardateinput>
			`]
		])}
	`;

}
